/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CallAgent,
  DeviceManager,
  Call,
  RemoteParticipant,
  LocalVideoStream,
  RemoteVideoStream,
  VideoStreamRenderer
} from '@azure/communication-calling';
import {
  Box,
  IconButton,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Videocam,
  VideocamOff,
  Mic,
  MicOff,
  CallEnd,
  SwapHoriz,
  Chat,
  ChatBubbleOutline,
  EditCalendar,
  Person,
  Phone,
  Group,
  PhoneDisabled,
  AccountCircle
} from '@mui/icons-material';
import TextMessagesChat from 'components/generals/TextMessagesChat';
import { CallTimer } from 'components/generals/CallTimer';
import { t } from 'i18next';
import { useMaterialUIController } from 'context/ThemeContext';
import { ChatMessageInterface } from '@malmur-technologies/medicalsystem-shared';
import { VideoRendererType, VideoScalingMode, VideoStreamEventArgs } from 'types/renderer.enum';

/**
 * Props interface for VideochatRenderer component.
 * Defines all configuration options for Azure Communication Services video calling.
 */
export interface VideochatRendererProps {
  call: Call | null;
  callAgent?: CallAgent | null;
  deviceManager?: DeviceManager | null;
  callState: string;
  participants: RemoteParticipant[];
  currentUserName: string;
  getParticipantName?: (participant?: RemoteParticipant) => string;
  chatMessages: ChatMessageInterface[];
  onSendMessage: (message: string) => void;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onEndCall: () => void;
  showChat?: boolean;
  showControls?: boolean;
  showSwapButton?: boolean;
  isMinimized?: boolean;
  className?: string;
  showProgressNoteButton?: boolean;
  onProgressNoteClick?: () => void;
  allowChatToggle?: boolean;
  onParticipantsUpdated?: (participants: RemoteParticipant[]) => void;
  onCallStateChanged?: (state: string) => void;
  isMaximized?: boolean;
  onMinimize?: () => void;
  onMaximize?: () => void;
  onRestore?: () => void;
  windowTitle?: string;
  showWindowControls?: boolean;
  guestJoinUrl?: string;
  callHasEnded?: boolean;
  canRejoin?: boolean;
  onRejoinCall?: () => void;
  hasLeftCall?: boolean;
  onJoinCall?: () => void;
  isGuestContext?: boolean;
  /** Minimum video height in pixels for responsive layouts */
  minVideoHeight?: number;
  /** Call start time from backend */
  callStartTime?: Date | null;
  /** Backend participant count from session */
  backendParticipantCount?: number;
}

/**
 * Main videochat renderer component using Azure Communication Services.
 * Manages video streams, audio/video controls, chat interface, and call state.
 * Supports minimized, maximized, and normal window modes with video swapping.
 * Handles both authenticated user and guest participant contexts.
 * 
 * @param props - VideochatRenderer configuration including call state, participants, and callbacks
 * @returns Rendered videochat interface with controls and video streams
 */
const VideochatRenderer: React.FC<VideochatRendererProps> = ({
  call,
  callState,
  participants,
  currentUserName,
  getParticipantName,
  chatMessages,
  onSendMessage,
  isVideoEnabled,
  isAudioEnabled,
  onToggleVideo,
  onToggleAudio,
  onEndCall,
  showChat = true,
  showControls = true,
  showSwapButton = false,
  isMinimized = false,
  className = '',
  showProgressNoteButton = false,
  onProgressNoteClick,
  allowChatToggle = false,
  isMaximized = false,
  onMinimize,
  onMaximize,
  onRestore,
  windowTitle = 'Videochat',
  showWindowControls = false,
  guestJoinUrl,
  callHasEnded,
  canRejoin,
  onRejoinCall,
  hasLeftCall,
  onJoinCall,
  isGuestContext,
  minVideoHeight,
  callStartTime,
  backendParticipantCount
}) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [isVideoSwapped, setIsVideoSwapped] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const minimizedVideoRef = useRef<HTMLDivElement>(null);
  const [currentRemoteStream, setCurrentRemoteStream] = useState<RemoteVideoStream | null>(null);
  const [currentLocalStream, setCurrentLocalStream] = useState<LocalVideoStream | null>(null);
  const [minimizedRenderer, setMinimizedRenderer] = useState<VideoStreamRenderer | null>(null);


  const renderersRef = useRef<{
    remote: VideoStreamRenderer | null;
    local: VideoStreamRenderer | null;
    minimized: VideoStreamRenderer | null;
  }>({
    remote: null,
    local: null,
    minimized: null
  });

  /**
   * Calculates minimum video height based on context and configuration.
   * @returns Minimum height in pixels (custom, guest context, or default)
   */
  const getMinHeight = () => {
    if (minVideoHeight) return minVideoHeight;
    if (isGuestContext) return 950;
    return 200;
  };

  const minHeight = getMinHeight();

  /**
   * Cleans up and disposes a video stream renderer to prevent memory leaks.
   * @param type - Type of renderer to cleanup (remote, local, or minimized)
   */
  const cleanupRenderer = useCallback((type: VideoRendererType) => {
    if (renderersRef.current[type]) {
      try {
        renderersRef.current[type].dispose();
      } catch (err) {
        console.warn(`Error disposing ${type} renderer:`, err);
      }
      renderersRef.current[type] = null;
    }
  }, []);

  /**
   * Cleans up all video stream renderers (remote, local, and minimized).
   * Called on component unmount to prevent memory leaks.
   */
  const cleanupAllRenderers = useCallback(() => {
    cleanupRenderer(VideoRendererType.REMOTE);
    cleanupRenderer(VideoRendererType.LOCAL);
    cleanupRenderer(VideoRendererType.MINIMIZED);
  }, [cleanupRenderer]);

  /**
   * Renders a video stream (local or remote) into a DOM element.
   * Creates VideoStreamRenderer with specified scaling mode and appends to target.
   * @param stream - Local or remote video stream to render
   * @param targetRef - React ref to DOM element where video will be rendered
   * @param rendererType - Type of renderer (remote, local, minimized)
   * @param scalingMode - Video scaling mode (crop or fit)
   */
  const renderVideoStream = useCallback(async (
    stream: LocalVideoStream | RemoteVideoStream,
    targetRef: React.RefObject<HTMLDivElement>,
    rendererType: VideoRendererType,
    scalingMode: VideoScalingMode.CROP
  ) => {
    if (!targetRef.current || !stream) return;

    try {
      cleanupRenderer(rendererType);
      targetRef.current.innerHTML = '';
      const { VideoStreamRenderer } = await import('@azure/communication-calling');
      const renderer = new VideoStreamRenderer(stream);
      const view = await renderer.createView({
        scalingMode: scalingMode === VideoScalingMode.CROP ? VideoScalingMode.CROP : VideoScalingMode.FIT
      });
      targetRef.current.appendChild(view.target);
      renderersRef.current[rendererType] = renderer;
    } catch (err) {
      console.error(`Error rendering ${rendererType} video stream:`, err);
    }
  }, [cleanupRenderer]);

  useEffect(() => {
    if (call && isVideoEnabled && call.localVideoStreams?.length > 0) {
      setCurrentLocalStream(call.localVideoStreams[0]);
    } else {
      setCurrentLocalStream(null);
    }
  }, [call, isVideoEnabled]);

  useEffect(() => {
    if (!call || participants.length === 0) {
      setCurrentRemoteStream(null);
      return;
    }
    const participant = participants[0];
    let mounted = true;
    const handleVideoStreamUpdate = (stream: RemoteVideoStream) => {
      if (!mounted) return;

      if (stream.isAvailable) {
        setCurrentRemoteStream(stream);
      } else {
        stream.on('isAvailableChanged', () => {
          if (mounted && stream.isAvailable) {
            setCurrentRemoteStream(stream);
          }
        });
      }
    };
    if (participant.videoStreams?.length > 0) {
      participant.videoStreams.forEach(handleVideoStreamUpdate);
    }
    const handleVideoStreamsUpdated = (e: VideoStreamEventArgs) => {
      if (!mounted) return;
      if (e.added?.length > 0) {
        e.added.forEach(handleVideoStreamUpdate);
      }
      if (e.removed?.length > 0) {
        setCurrentRemoteStream(null);
      }
    };
    participant.on('videoStreamsUpdated', handleVideoStreamsUpdated);
    return () => {
      mounted = false;
      participant.off('videoStreamsUpdated', handleVideoStreamsUpdated);
    };
  }, [call, participants]);

  useEffect(() => {
    if (isMinimized) return;
    let timeoutId: NodeJS.Timeout;

    const renderVideos = async () => {
      const remoteStream = isVideoSwapped ? currentLocalStream : currentRemoteStream;
      const localStream = isVideoSwapped ? currentRemoteStream : currentLocalStream;

      if (remoteStream && remoteVideoRef.current) {
        await renderVideoStream(remoteStream, remoteVideoRef, VideoRendererType.REMOTE, VideoScalingMode.CROP);
      } else {
        cleanupRenderer(VideoRendererType.REMOTE);
        if (remoteVideoRef.current) remoteVideoRef.current.innerHTML = '';
      }

      if (localStream && localVideoRef.current && (isVideoSwapped || isVideoEnabled)) {
        await renderVideoStream(localStream, localVideoRef, VideoRendererType.LOCAL, VideoScalingMode.CROP);
      } else {
        cleanupRenderer(VideoRendererType.LOCAL);
        if (localVideoRef.current) localVideoRef.current.innerHTML = '';
      }
    };

    timeoutId = setTimeout(renderVideos, 100);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentRemoteStream, currentLocalStream, isVideoSwapped, isVideoEnabled, isMinimized, renderVideoStream, cleanupRenderer]);

  useEffect(() => {
    if (!isMinimized) {
      cleanupRenderer(VideoRendererType.MINIMIZED);
      return;
    }
    const renderMinimizedVideoNow = async () => {
      if (!minimizedVideoRef.current) return;
      cleanupRenderer(VideoRendererType.MINIMIZED);
      minimizedVideoRef.current.innerHTML = '';
      try {
        let streamToRender = null;
        if (currentRemoteStream) {
          streamToRender = currentRemoteStream;
        }
        else if (currentLocalStream && isVideoEnabled) {
          streamToRender = currentLocalStream;
        }
        if (streamToRender) {
          const { VideoStreamRenderer } = await import('@azure/communication-calling');
          const renderer = new VideoStreamRenderer(streamToRender);
          const view = await renderer.createView({ scalingMode: 'Crop' });

          if (minimizedVideoRef.current && isMinimized) {
            minimizedVideoRef.current.appendChild(view.target);
            renderersRef.current.minimized = renderer;
            setMinimizedRenderer(renderer);
          } else {
            renderer.dispose();
          }
        }
      } catch (error) {
        console.error('Error rendering minimized video:', error);
      }
    };
    renderMinimizedVideoNow();
  }, [isMinimized, currentRemoteStream, currentLocalStream, isVideoEnabled, cleanupRenderer]);

  useEffect(() => {
    if (!isChatOpen) {
      const incomingFromOthers = chatMessages.filter(msg => !msg.isOwn);
      setUnreadMessages(incomingFromOthers.length);
    } else {
      setUnreadMessages(0);
    }
  }, [chatMessages, isChatOpen]);

  useEffect(() => {
    return () => {
      cleanupAllRenderers();
    };
  }, [cleanupAllRenderers]);

  /**
   * Swaps the positions of local and remote video streams.
   * Cleans up current renderers before swapping to prevent conflicts.
   */
  const swapVideoStreams = useCallback(() => {
    cleanupAllRenderers();
    setTimeout(() => {
      setIsVideoSwapped(prev => !prev);
    }, 50);
  }, [isVideoSwapped, currentRemoteStream, currentLocalStream, cleanupAllRenderers]);

  /**
   * Gets the display name for the main video participant.
   * Uses custom name function if provided, otherwise returns default text.
   * @returns Participant display name or default message
   */
  const getMainParticipantName = () => {
    if (getParticipantName && participants.length > 0) {
      return getParticipantName(participants[0]);
    }
    return participants.length > 0 ? 'Participante remoto' : 'Esperando participante...';
  };

  /**
   * Gets the display name for the minimized video window.
   * Shows remote participant name if available, otherwise current user name.
   * @returns Display name for minimized view
   */
  const getMinimizedDisplayName = () => {
    if (currentRemoteStream) {
      return getParticipantName ? getParticipantName(participants[0]) : 'Participante remoto';
    }
    if (currentLocalStream && isVideoEnabled) {
      return currentUserName;
    }
    return 'Esperando participante...';
  };

  /**
   * Toggles the chat panel visibility.
   * Resets unread message count when opening chat.
   */
  const toggleChat = () => {
    setIsChatOpen(prev => !prev);
  };

  /**
   * Renders the videochat window header with title, status, and window controls.
   * Includes minimize, maximize/restore, and close buttons.
   * @returns Window header component or null if not shown
   */
  const WindowHeader = () => (
    showWindowControls && (
      <Box className={`videochat-header ${darkMode ? 'videochat-header--dark' : ''} ${isMaximized ? 'videochat-header--maximized' : ''}`}>
        <Box className="videochat-header__title-section">
          <svg className="videochat-header__icon" width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M17 10.5V7C17 4.24 14.76 2 12 2S7 4.24 7 7V10.5C4.24 10.5 2 12.74 2 15.5V19C2 19.55 2.45 20 3 20H21C21.55 20 22 19.55 22 19V15.5C22 12.74 19.76 10.5 17 10.5ZM6 12H18V14H6V12Z" />
          </svg>
          <h2 className="videochat-header__title">
            {windowTitle}
          </h2>
          <span className={`videochat-header__status ${callState === t('videochat.connected') ? 'videochat-header__status--connected' : 'videochat-header__status--default'}`}>
            {callState}
          </span>
        </Box>
        <Box className="videochat-header__controls">
          {onMinimize && (
            <IconButton
              id="cy-minimize-window"
              onClick={onMinimize}
              title={t('videochat.minimize')}
              className="videochat-header-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white">
                <path d="M6 12H18V14H6V12Z" />
              </svg>
            </IconButton>
          )}
          {(onMaximize || onRestore) && (
            <IconButton
              onClick={isMaximized ? onRestore : onMaximize}
              title={isMaximized ? t('videochat.restore') : t('videochat.maximize')}
              className="videochat-header-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white">
                {isMaximized ? (
                  <g>
                    <path d="M6 6H16V16H6V6ZM8 8V14H14V8H8Z" />
                    <path d="M10 4H18V12H16V6H10V4Z" />
                  </g>
                ) : (
                  <path d="M6 6H18V18H6V6ZM8 8V16H16V8H8Z" />
                )}
              </svg>
            </IconButton>
          )}
          <IconButton
            id="cy-end-call"
            onClick={onEndCall}
            title={t('videochat.leaveCall')}
            className="videochat-header-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white">
              <path d="M18 6L6 18M6 6L18 18" strokeWidth="2" />
            </svg>
          </IconButton>
        </Box>
      </Box>
    )
  );
  if (isMinimized) {
    return (
      <Box className={`${className} videochat-renderer--minimized`}>
        <Box className="videochat-minimized-header">
          <span>{t('videochat.minimizedTitle')}</span>
          {onRestore && (
            <IconButton
              id="cy-maximize-window"
              onClick={onRestore}
              title={t('videochat.restore')}
              className="videochat-minimized-restore-button"
            >
              <span>□</span>
            </IconButton>
          )}
        </Box>
        <Box className="videochat-minimized-video">
          <div
            ref={minimizedVideoRef}
            className="videochat-minimized-video-container"
          />
          {!currentRemoteStream && !(currentLocalStream && isVideoEnabled) && (
            <Box className="videochat-minimized-placeholder">
              <AccountCircle className="videochat-minimized-placeholder__icon" />
              <div className="videochat-minimized-placeholder__text">{t('videochat.waitingForOtherParticipant')}</div>
            </Box>
          )}
          {(currentRemoteStream || (currentLocalStream && isVideoEnabled)) && (
            <Box className="videochat-minimized-label">
              <span>
                {getMinimizedDisplayName()}
              </span>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
  return (
    <Box className={`${className} videochat-renderer ${isMaximized ? 'videochat-renderer--maximized' : ''} ${isGuestContext ? 'videochat-renderer--guest' : ''}`}>
      <WindowHeader />
      {guestJoinUrl && (
        <Box className={`videochat-guest-link ${darkMode ? 'videochat-guest-link--dark' : ''}`}>
          <Box component="span" className="videochat-guest-link__label">
            {t('videochat.guestLink')}
          </Box>
          <Box className="videochat-guest-link__content">
            <Box component="span" className="videochat-guest-link__url">
              {guestJoinUrl}
            </Box>
            <IconButton
              onClick={() => navigator.clipboard.writeText(guestJoinUrl)}
              title={t('videochat.copyGuestLink')}
              className={`videochat-guest-link__copy-button ${darkMode ? 'videochat-guest-link__copy-button--dark' : ''}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={darkMode ? '#fff' : '#333'}>
                <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      )}
      <Box className="videochat-layout">
        <Box className="videochat-video-section">
          <Paper className={`videochat-main-video ${!showControls ? 'videochat-main-video--no-controls' : ''} ${showWindowControls ? 'videochat-main-video--no-border' : ''} ${isGuestContext ? 'videochat-main-video--guest' : ''}`}>
            <div
              ref={remoteVideoRef}
              className={`videochat-video-container ${isGuestContext ? 'videochat-video-container--guest' : ''}`}
              data-video-container="remote"
            />
            {(participants.length === 0 && !hasLeftCall) && (
              <Box className="videochat-status-message">
                <Group className="videochat-status-message__icon" />
                <div className="videochat-status-message__text">
                  {t('videochat.waitingForOtherParticipant')}
                </div>
              </Box>
            )}

            {hasLeftCall && (
              <Box className="videochat-status-message">
                <PhoneDisabled className="videochat-status-message__icon" />
                <div className="videochat-status-message__text">
                  {t('videochat.youHaveLeftTheCall')}
                </div>
              </Box>
            )}

            {hasLeftCall && (
              <Box className="videochat-status-message">
                <PhoneDisabled className="videochat-status-message__icon" />
                <div className="videochat-status-message__text">
                  {t('videochat.youHaveLeftTheCall')}
                </div>
              </Box>
            )}
            <Box className="videochat-participant-label">
              <Person />
              <span>
                {(() => {
                  // Use backend participant count if available
                  if (backendParticipantCount !== undefined && backendParticipantCount !== null) {
                    console.log('[PARTICIPANT COUNT] Using backend count:', backendParticipantCount);
                    return backendParticipantCount;
                  }

                  // Fallback: Count unique participants by their identifier to avoid duplicates
                  if (!call || !call.remoteParticipants) return 1;
                  const uniqueParticipants = new Set();
                  call.remoteParticipants.forEach((participant: any) => {
                    const id = participant.identifier?.communicationUserId || participant.identifier?.phoneNumber;
                    if (id) uniqueParticipants.add(id);
                  });
                  const clientCount = uniqueParticipants.size + 1; // +1 for local user
                  console.log('[PARTICIPANT COUNT] Using client-side count:', clientCount);
                  return clientCount;
                })()}
              </span>
            </Box>
            {participants.length > 0 && (
              <Box className="videochat-participant-name">
                <span>
                  {isVideoSwapped ? currentUserName : getMainParticipantName()}
                </span>
              </Box>
            )}
            {showSwapButton && participants.length > 0 && (
              <IconButton
                onClick={swapVideoStreams}
                title={t('videochat.swapVideo')}
                className="videochat-swap-button"
              >
                <SwapHoriz />
              </IconButton>
            )}
            {callState === 'Connected' && (
              <Box className="videochat-timer">
                <CallTimer
                  isCallActive={true}
                  callStartTime={callStartTime}
                />
              </Box>
            )}
          </Paper>
          <Box className="videochat-local-video">
            <Paper
              title={t('videochat.swapVideo')}
              className="videochat-local-video-paper"
              onClick={swapVideoStreams}
            >
              <div
                ref={localVideoRef}
                className="videochat-local-video-container"
              />

              {(!isVideoSwapped && !isVideoEnabled) && (
                <Box className="videochat-local-video-placeholder">
                  <VideocamOff className="videochat-local-video-placeholder__icon" />
                  <div className="videochat-local-video-placeholder__text">{t('videochat.videoDisabled')}</div>
                </Box>
              )}
              <Box className="videochat-local-video-label">
                <span>
                  {isVideoSwapped ? getMainParticipantName() : currentUserName}
                </span>
              </Box>
            </Paper>
          </Box>
          {showControls && (
            <Box className={`videochat-controls ${showWindowControls ? 'videochat-controls--no-border' : ''}`}>
              <IconButton
                id="cy-toggle-audio"
                onClick={onToggleAudio}
                title={isAudioEnabled ? t('videochat.muteAudio') : t('videochat.unmuteAudio')}
                className={`videochat-control-button ${isAudioEnabled ? (darkMode ? 'videochat-control-button--enabled-dark' : 'videochat-control-button--enabled') : 'videochat-control-button--disabled'}`}
              >
                {isAudioEnabled ? <Mic /> : <MicOff />}
              </IconButton>
              <IconButton
                onClick={onToggleVideo}
                title={isVideoEnabled ? t('videochat.disableVideo') : t('videochat.enableVideo')}
                className={`videochat-control-button ${isVideoEnabled ? (darkMode ? 'videochat-control-button--enabled-dark' : 'videochat-control-button--enabled') : 'videochat-control-button--disabled'}`}
              >
                {isVideoEnabled ? <Videocam /> : <VideocamOff />}
              </IconButton>
              <IconButton
                id="cy-toggle-call"
                onClick={hasLeftCall ? onJoinCall : onEndCall}
                title={hasLeftCall ? t('videochat.joinCall') : t('videochat.leaveCall')}
                className={`videochat-control-button ${hasLeftCall ? 'videochat-control-button--success' : 'videochat-control-button--disabled'}`}
              >
                {hasLeftCall ? <Phone /> : <CallEnd />}
              </IconButton>
              {showProgressNoteButton && onProgressNoteClick && (
                <IconButton
                  id="cy-openProgressNote"
                  onClick={onProgressNoteClick}
                  title={t('videochat.progressNote')}
                  className={`videochat-control-button ${darkMode ? 'videochat-control-button--enabled-dark' : 'videochat-control-button--enabled'}`}
                >
                  <EditCalendar />
                </IconButton>
              )}
              {allowChatToggle && (
                <IconButton
                  id="cy-toggle-chat"
                  onClick={toggleChat}
                  title={isChatOpen ? t('videochat.closeChat') : t('videochat.openChat')}
                  className={`videochat-chat-toggle ${darkMode ? 'videochat-chat-toggle--dark' : ''}`}
                >
                  {isChatOpen ? <Chat /> : <ChatBubbleOutline />}
                  {unreadMessages > 0 && (
                    <Box className="videochat-unread-badge">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </Box>
                  )}
                </IconButton>
              )}
            </Box>
          )}
        </Box>
        {(showChat || (allowChatToggle && isChatOpen)) && (
          <Box className={`videochat-chat-panel ${darkMode ? 'videochat-chat-panel--dark' : ''} ${showWindowControls ? 'videochat-chat-panel--no-border' : ''}`}>
            <TextMessagesChat
              isOpen={true}
              onClose={allowChatToggle ? toggleChat : () => { }}
              messages={chatMessages}
              onSendMessage={onSendMessage}
              currentUser={currentUserName}
              disabled={callState !== 'Connected'}
              showCloseButton={allowChatToggle}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default VideochatRenderer;